// WebAuthn (passkey) helpers.
// The browser's navigator.credentials API works with ArrayBuffers while the
// backend carries the same fields as base64 strings; these functions convert
// between the two sides.

// Its message carries an i18n key, so the caller can hand the error straight
// to UnexpectedError.
export class PasskeyError extends Error {
  constructor(messageKey) {
    super(messageKey);
    this.name = "PasskeyError";
  }
}

// Thrown by a scoped passkey login when the account has no passkey to offer.
export const NO_PASSKEY_MESSAGE_KEY = "login:passkey.errors.no_passkey";

export const isWebAuthnSupported = () =>
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials?.create;

export const isWebAuthnLoginSupported = () =>
  typeof window !== "undefined" &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials?.get;

// Whether the browser can list passkeys in an input's autofill dropdown
// (conditional mediation, the "webauthn" autocomplete token).
export const isConditionalMediationAvailable = async () => {
  if (
    !isWebAuthnLoginSupported() ||
    typeof window.PublicKeyCredential.isConditionalMediationAvailable !==
      "function"
  ) {
    return false;
  }

  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
};

// Tells the device's passkey provider that the server no longer knows this
// credential, so it can stop offering it at sign-in. Best effort: browsers
// without the Signal API, and providers that ignore it, keep the entry.
export const signalUnknownCredential = (rpId, credentialId) => {
  if (
    typeof window === "undefined" ||
    typeof window.PublicKeyCredential?.signalUnknownCredential !== "function" ||
    !rpId ||
    !credentialId
  ) {
    return;
  }

  try {
    window.PublicKeyCredential.signalUnknownCredential({
      rpId,
      credentialId
    }).catch(() => {});
  } catch (error) {
    // Nothing to recover - the entry simply stays on the device.
  }
};

// Accepts both base64url and plain base64 input, so it keeps working
// whichever one the backend returns.
export const base64UrlToBuffer = value => {
  const normalized = String(value ?? "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding ? normalized + "=".repeat(4 - padding) : normalized;

  return Uint8Array.from(atob(padded), char => char.charCodeAt(0));
};

export const bufferToBase64Url = buffer => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// Converts the JSON coming from the server into the
// PublicKeyCredentialCreationOptions that navigator.credentials.create expects.
export const toCreationOptions = options => {
  const creationOptions = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64UrlToBuffer(options.user.id)
    }
  };

  if (Array.isArray(options.excludeCredentials)) {
    creationOptions.excludeCredentials = options.excludeCredentials.map(
      credential => ({
        ...credential,
        id: base64UrlToBuffer(credential.id)
      })
    );
  }

  return creationOptions;
};

// Converts the JSON coming from the server into the
// PublicKeyCredentialRequestOptions that navigator.credentials.get expects.
// Every other field the server sends (rpId, userVerification, timeout) is
// carried over untouched.
export const toRequestOptions = options => {
  const requestOptions = {
    ...options,
    challenge: base64UrlToBuffer(options.challenge)
  };

  // The usernameless login flow leaves this out - the user picks any
  // discoverable credential - but a scoped ceremony would carry it.
  if (Array.isArray(options.allowCredentials)) {
    requestOptions.allowCredentials = options.allowCredentials.map(
      credential => ({
        ...credential,
        id: base64UrlToBuffer(credential.id)
      })
    );
  }

  return requestOptions;
};

// Converts the PublicKeyCredential returned by the browser into the body the
// backend expects. Recent browsers produce the same shape via native toJSON.
export const credentialToJSON = credential => {
  if (typeof credential.toJSON === "function") {
    return credential.toJSON();
  }

  const { response } = credential;

  return {
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports:
        typeof response.getTransports === "function"
          ? response.getTransports()
          : []
    },
    clientExtensionResults:
      typeof credential.getClientExtensionResults === "function"
        ? credential.getClientExtensionResults()
        : {}
  };
};

// The login counterpart of credentialToJSON. An assertion carries
// authenticatorData/signature/userHandle instead of an attestationObject.
export const assertionToJSON = credential => {
  if (typeof credential.toJSON === "function") {
    return credential.toJSON();
  }

  const { response } = credential;

  return {
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      // Absent when the authenticator has no user handle for this credential.
      userHandle: response.userHandle
        ? bufferToBase64Url(response.userHandle)
        : null
    },
    clientExtensionResults:
      typeof credential.getClientExtensionResults === "function"
        ? credential.getClientExtensionResults()
        : {}
  };
};

export const getWebAuthnErrorKey = error => {
  switch (error?.name) {
    case "NotAllowedError":
    case "AbortError":
      return "settings:passkeys.errors.cancelled";

    case "InvalidStateError":
      return "settings:passkeys.errors.already_registered";

    // The device cannot meet the requirements the server asked for (resident
    // key / user verification) - e.g. an old security key without a PIN.
    case "ConstraintError":
      return "settings:passkeys.errors.constraint";

    case "NotSupportedError":
      return "settings:passkeys.errors.not_supported";

    case "SecurityError":
      return "settings:passkeys.errors.security";

    default:
      return "generic:errors.unexpected";
  }
};
